import React, { useState } from 'react';
import { Clock, Users, Star, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { tours } from '../mock/mockData';

const ToursLuxury = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Experiences' },
    { id: 'adventure', label: 'Adventure' },
    { id: 'sightseeing', label: 'Scenic & Cultural' },
    { id: 'luxury', label: 'Luxury & Wine' }
  ];

  const filteredTours = activeCategory === 'all' 
    ? tours 
    : tours.filter(tour => tour.category === activeCategory);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="tours" className="relative py-20 bg-gradient-to-b from-white via-[#FAF8F5] to-white overflow-hidden">
      {/* Decorative Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%236B0B0B' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        backgroundSize: '60px 60px'
      }}></div>
      
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/5 via-transparent to-[#6B0B0B]/5"></div>
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full text-[#C5A059] font-medium text-sm tracking-wide">
              CURATED EXPERIENCES
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-[#0B090A] mb-6 tracking-tight">
            Extraordinary Cape Town
            <span className="block text-[#6B0B0B]">Adventures</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From thrilling marine encounters to refined wine estates, discover handpicked 
            experiences that showcase the very best of the Mother City
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeCategory === category.id
                  ? 'bg-[#6B0B0B] text-white shadow-lg shadow-[#6B0B0B]/30'
                  : 'bg-white text-gray-700 hover:bg-[#D4AF37]/10 hover:text-[#6B0B0B] border border-gray-200'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>

        {/* Tours Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredTours.map((tour, index) => (
            <div
              key={tour.id}
              className="group relative bg-white rounded-2xl overflow-hidden cinema-shadow hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-[#D4AF37]/20"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={tour.image}
                  alt={tour.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                
                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-[#6B0B0B] text-white text-xs font-medium rounded-full backdrop-blur-sm">
                    {tour.category.toUpperCase()}
                  </span>
                </div>

                {/* Price Badge */}
                <div className="absolute top-4 right-4 text-right">
                  <div className="glass-card px-4 py-2">
                    {tour.priceType === 'per-group' ? (
                      <>
                        <div className="text-sm text-[#D4AF37] font-medium">Per Group</div>
                        <div className="text-lg font-bold text-white">R{tour.price.toLocaleString()}</div>
                      </>
                    ) : tour.pricingType === 'age-based' ? (
                      <>
                        <div className="text-sm text-[#D4AF37] font-medium">From</div>
                        <div className="text-lg font-bold text-white">R{tour.priceKids.toLocaleString()}</div>
                      </>
                    ) : tour.dualPricing ? (
                      <>
                        <div className="text-sm text-[#D4AF37] font-medium">From</div>
                        <div className="text-lg font-bold text-white">R{tour.price.toLocaleString()}</div>
                      </>
                    ) : (
                      <>
                        <div className="text-sm text-[#D4AF37] font-medium">Per Person</div>
                        <div className="text-lg font-bold text-white">R{tour.price.toLocaleString()}</div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Title */}
                <h3 className="text-xl font-serif font-bold text-[#0B090A] mb-3 group-hover:text-[#6B0B0B] transition-colors">
                  {tour.title}
                </h3>

                {/* Description */}
                <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                  {tour.description}
                </p>

                {/* Meta Info */}
                <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Clock size={16} className="text-[#D4AF37]" />
                    <span>{tour.duration}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Star size={16} className="text-[#D4AF37]" fill="#D4AF37" />
                    <span>5.0</span>
                  </div>
                </div>

                {/* Highlights */}
                <div className="mb-4">
                  <ul className="space-y-2">
                    {tour.highlights.slice(0, 3).map((highlight, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <ChevronRight size={16} className="text-[#6B0B0B] flex-shrink-0 mt-0.5" />
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Special Note */}
                {tour.specialNote && (
                  <div className="mb-4 p-3 bg-[#D4AF37]/10 border-l-4 border-[#D4AF37] rounded">
                    <p className="text-xs text-gray-700">{tour.specialNote}</p>
                  </div>
                )}

                {/* CTA Button */}
                <Button
                  onClick={scrollToContact}
                  className="w-full bg-[#6B0B0B] hover:bg-[#8B1515] text-white py-6 font-semibold rounded-full transition-all duration-300 group-hover:shadow-lg group-hover:shadow-[#6B0B0B]/30"
                >
                  Book This Experience
                  <ChevronRight size={18} className="ml-2" />
                </Button>
              </div>

              {/* Decorative Gold Border on Hover */}
              <div className="absolute inset-0 rounded-2xl border-2 border-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="inline-block p-8 glass-card rounded-3xl">
            <h3 className="text-2xl font-serif font-bold text-[#0B090A] mb-3">
              Can't Find What You're Looking For?
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl">
              We specialize in bespoke luxury experiences tailored to your preferences
            </p>
            <Button
              onClick={scrollToContact}
              className="px-8 py-6 bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B090A] font-semibold rounded-full text-lg transition-all duration-300 hover:shadow-xl"
            >
              Request Custom Experience
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ToursLuxury;
